import { useState } from "react"
import CodeMirror, { EditorView, type Extension } from "@uiw/react-codemirror"
import { CheckIcon, XIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ToMarkdown } from "@/components/ui/tomarkdown"
import { cn } from "@/lib/utils"

// Matches the surrounding text size instead of CodeMirror's smaller default
const fontSize = EditorView.theme({ "&": { fontSize: "0.9rem" } })

interface Props {
  value: string
  onChange: (value: string) => void
  extensions?: Extension[]
  placeholder?: string
  className?: string
  plain?: boolean                                   // at rest shows the text as written, new lines included, instead of markdown
}

// Always CodeMirror when editing, always ToMarkdown at rest unless plain. What varies per field is only
// which extensions get passed in (template autocomplete vs none), never the editor itself
export function EditableText({ value, onChange, extensions = [], placeholder = "Click to edit", className, plain }: Props) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  function startEditing() {
    setDraft(value)
    setEditing(true)
  }

  function accept() {
    onChange(draft)
    setEditing(false)
  }

  function cancel() {
    setEditing(false)
  }

  if (!editing) {
    return <div
      className={cn("rounded-md border border-transparent p-2 max-h-[33vh] overflow-y-auto cursor-text hover:border-dashed hover:border-muted-foreground/50", className)}
      onClick={startEditing}
    >
      {!value ? <span className="text-muted-foreground">{placeholder}</span> : plain ? <div className="whitespace-pre-wrap">{value}</div> : <ToMarkdown>{value}</ToMarkdown>}
    </div>
  }

  return <div className={cn("flex flex-col gap-1", className)}>
    <div className="rounded-md border border-foreground/30 overflow-hidden">
      <CodeMirror
        value={draft}
        onChange={setDraft}
        extensions={[EditorView.lineWrapping, fontSize, ...extensions]}
        basicSetup={{ lineNumbers: false, foldGutter: false }}
        height="auto"
        maxHeight="33vh"
        autoFocus
      />
    </div>
    <div className="flex justify-end gap-1">
      <Button variant="ghost" size="icon-xs" aria-label="Cancel" onClick={cancel}><XIcon /></Button>
      <Button variant="ghost" size="icon-xs" aria-label="Accept" onClick={accept}><CheckIcon /></Button>
    </div>
  </div>
}
